/**
 * Email System Type Definitions
 * Matches the database schema and defines all email-related types
 */

// Email trigger types based on requirements
export const EMAIL_TRIGGER_TYPES = {
  // Booking confirmations
  BOOKING_CONFIRMATION: "booking_confirmation",
  BOOKING_PRELIMINARY: "booking_preliminary",
  BOOKING_CONFIRMED_CORPORATE: "booking_confirmed_corporate",
  BOOKING_CONFIRMED_PRIVATE: "booking_confirmed_private",
  BOOKING_CONFIRMED_ROADSHOW: "booking_confirmed_roadshow",

  // Database trigger types (actual templates in DB)
  RESERVED_CORPORATE: "reserved_corporate",
  RESERVED_PRIVATE: "reserved_private",
  RESERVED_ROADSHOW: "reserved_roadshow",
  PRELIMINARY_RESERVATION: "preliminary_reservation",

  // Updates
  BOOKING_UPDATED_MINOR: "booking_updated_minor",
  BOOKING_UPDATED_MAJOR: "booking_updated_major",
  UPDATED_SMALL_CHANGES: "updated_small_changes",
  UPDATED_TIME_CHANGE: "updated_time_change",

  // Reminders
  REMINDER_TIPS_CORPORATE: "reminder_tips_corporate",
  REMINDER_TIPS_PRIVATE: "reminder_tips_private",
  REMINDER_FINAL: "reminder_final",

  // Database reminder types (actual templates in DB)
  REMINDER_1D_CORPORATE: "reminder_1d_corporate",
  REMINDER_1D_PRIVATE: "reminder_1d_private",
  REMINDER_3D_CORPORATE: "reminder_3d_corporate",
  REMINDER_3D_PRIVATE: "reminder_3d_private",
  REMINDER_7D: "reminder_7d",
  REMINDER_2H: "reminder_2h",
  REMINDER_24H: "reminder_24h",

  // Invoice reminders
  INVOICE_INFO_REMINDER: "invoice_info_reminder",

  // Follow-up — "Thanks for visiting" email, sent 3h after show ends.
  // String value matches `email_templates.trigger_type` in the DB.
  POST_VISIT_REVIEW: "thanks_for_visiting",

  // Special add-ons
  ADDON_SPECIAL_QUESTIONS: "addon_special_questions",

  // Cancellation
  BOOKING_CANCELLATION: "booking_cancellation",

  // Road show specific
  BOOKING_MODIFICATION_ROADSHOW: "booking_modification_roadshow",
  REMINDER_3D_ROADSHOW: "reminder_3d_roadshow",
  REMINDER_1D_ROADSHOW: "reminder_1d_roadshow",
  POST_SHOW_ROADSHOW: "post_show_roadshow",
  CANCELLATION_ROADSHOW: "cancellation_roadshow",
} as const;

export type EmailTriggerType =
  (typeof EMAIL_TRIGGER_TYPES)[keyof typeof EMAIL_TRIGGER_TYPES];

// Email template - matches database schema
export interface EmailTemplate {
  id: number;
  venue_id: number;
  show_id: number | null;
  trigger_type: string;
  name: string;
  subject: string;
  body: string;
  from_name: string | null;
  reply_to: string | null;
  cc_emails: string | null;
  status_id: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

// Email template with relations
export interface EmailTemplateWithRelations extends EmailTemplate {
  venue?: {
    id: number;
    name: string;
  };
  show?: {
    id: number;
    name: string;
  } | null;
}

// Email log - matches database schema
export interface EmailLog {
  id: number;
  email_template_id: number | null;
  booking_id: number | null;
  recipient_email: string;
  recipient_name: string | null;
  subject: string;
  body: string;
  status: EmailStatus;
  sent_at: string | null;
  error_message: string | null;
  created_at: string;
  // Extended fields for Postmark tracking
  provider_message_id?: string | null;
  opened_at?: string | null;
  clicked_at?: string | null;
  bounced_at?: string | null;
  bounce_type?: string | null;
  metadata?: Record<string, any> | null;
}

export type EmailStatus =
  | "pending"
  | "sent"
  | "failed"
  | "bounced"
  | "delivered";

// Email template variable - matches database schema
export interface EmailTemplateVariable {
  id: number;
  variable_name: string;
  description: string | null;
  category: EmailVariableCategory | null;
  example_value: string | null;
  created_at: string;
}

export type EmailVariableCategory =
  | "customer"
  | "booking"
  | "show"
  | "venue"
  | "financial"
  | "system";

// Variable definitions for template rendering
export interface EmailVariables {
  // Customer variables
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  company_name?: string;

  // Booking variables
  booking_number: string;
  booking_date: string;
  booking_time: string;
  participants: string;
  booking_type: "corporate" | "private" | "roadshow";
  special_request?: string; // Road show location/details
  payment_method?: string; // Payment method (card, cash, invoice, bank_transfer)

  // Show variables
  show_names: string;
  show_duration?: string;
  min_charge?: string;

  // Venue variables
  venue_name: string;
  venue_address: string;
  venue_phone: string;
  venue_email: string;

  // Financial variables
  total_price: string;
  base_amount: string;
  vat_amount: string;
  vat_rate: string;
  currency: string;
  total_amount_inc_vat?: string; // Total including VAT for all products (shows + add-ons)

  // System variables
  portal_link: string;
  cancel_link: string;
  review_link?: string;
  survey_link?: string;
  cancellation_policy: string;

  // Dynamic variables for updates
  change_type?: string;
  old_date?: string;
  new_date?: string;
  old_time?: string;
  new_time?: string;

  // Additional template compatibility variables
  booking_id?: string;
  show_name?: string; // Alternative to show_names
  num_participants?: string; // Alternative to participants
  show_price?: string;
  addons_total?: string;
  total_amount?: string; // Alternative to total_price

  // Any additional custom variables
  [key: string]: string | undefined;
}

// Email content for different languages
export interface LocalizedEmailContent {
  subject: string;
  body: string;
  language: string;
}

// Complete email data for sending
export interface PreparedEmail {
  to: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  tag: string;
  metadata: {
    bookingId?: number;
    customerId?: number;
    emailLogId?: number;
    triggerType: EmailTriggerType;
    venueId: number;
  };
}

// Email template creation/update
export interface CreateEmailTemplateInput {
  venue_id: number;
  show_id?: number | null;
  trigger_type: EmailTriggerType;
  name: string;
  subject: string;
  body: string;
  from_name?: string | null;
  reply_to?: string | null;
  status_id: number;
}

export interface UpdateEmailTemplateInput
  extends Partial<CreateEmailTemplateInput> {
  id: number;
}

// Email sending request
export interface SendEmailRequest {
  bookingId: number;
  triggerType: EmailTriggerType;
  customVariables?: Record<string, string>;
}

// Email preview request
export interface PreviewEmailRequest {
  templateId: number;
  sampleData?: Partial<EmailVariables>;
}

// Booking data needed for emails
export interface BookingEmailData {
  id: number;
  booking_number: string;
  date: string;
  start_time: string;
  end_time: string;
  participants: number;
  type: "corporate" | "private" | "roadshow";
  customer_type_id?: number;
  price_type_id?: number;
  total_price: number;
  base_amount_excl_vat?: number;
  vat_amount?: number;
  vat_rate?: number;
  currency?: string;
  display_label?: string; // "incl. VAT" or "excl. VAT"
  created_at?: string;
  special_request?: string; // Road show location/details from bookings table
  payment_method?: string; // Payment method (card, cash, invoice, bank_transfer)

  customer: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    company_name?: string;
    preferred_language?: string;
  };

  venue: {
    id: number;
    name: string;
    address: string;
    phone: string;
    email: string;
    supported_languages?: string[];
  };

  shows: Array<{
    id: number;
    name: string;
    description?: string;
    duration?: string;
    min_participants?: number;
    max_participants?: number;
    pricing?: {
      price: number;
      vat_percentage?: number;
    };
  }>;

  studio?: {
    id: number;
    name: string;
    min_participants?: number;
  };

  add_ons?: Array<{
    id: number;
    name: string;
    quantity: number;
    price: number;
  }>;
}

// Email statistics
export interface EmailStats {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  failed: number;
}

// Webhook handling
export interface HandleWebhookResult {
  success: boolean;
  emailLogId?: number;
  error?: string;
}

// Admin UI types
export interface EmailTemplateFilter {
  venueId?: number;
  showId?: number;
  triggerType?: EmailTriggerType;
  statusId?: number;
}

export interface EmailLogFilter {
  venueId?: number;
  bookingId?: number;
  status?: EmailStatus;
  dateFrom?: string;
  dateTo?: string;
  recipientEmail?: string;
}
